// PRD.323 — presentation settings for one placed Render-mode mapping.
//
// ONE INTERPRETATION, THREE SURFACES. Map Fields, Preview and the final
// TemplateOverlayView each used to parse `form_template_mappings.config` and
// build their own `justifyMap` inline — three copies of the same rule, free to
// drift. Everything here is resolved once and consumed by all three, so what an
// admin positions is exactly what the patient's form prints.
//
// PRESENTATION ONLY. This never touches `field.label`: the canonical field
// label belongs to the form definition and is shared by Form mode, bindings and
// every other consumer. A Form Label is what THIS placement shows on THIS
// template, which is why it is stored per mapping.
//
// Stored inside the existing `config` jsonb — no schema change, and PRD.321's
// clone service copies `config` verbatim, so these settings clone for free.
//
// Import-free so it runs under the node:test harness.

export const MAPPING_ALIGNMENTS = ['left', 'center', 'right'] as const;
export type MappingAlignment = (typeof MAPPING_ALIGNMENTS)[number];

export const MAPPING_LABEL_POSITIONS = ['above', 'left'] as const;
export type MappingLabelPosition = (typeof MAPPING_LABEL_POSITIONS)[number];

/**
 * Historic default.
 *
 * Alignment predates this PRD and has always defaulted to `center` when
 * `config.textAlign` is absent. Changing it would silently re-position every
 * existing mapping on every published form, so it stays.
 */
export const DEFAULT_MAPPING_ALIGNMENT: MappingAlignment = 'center';

export interface MappingPresentation {
  /** Horizontal placement of the VALUE inside the mapping box. */
  alignment: MappingAlignment;
  /** Whether a Form Label is drawn. Off for every pre-existing mapping. */
  showLabel: boolean;
  /**
   * Label text as authored. Empty means "not customised yet" — the caller
   * falls back to the field label via `resolveMappingLabelText`.
   */
  labelText: string;
  labelPosition: MappingLabelPosition;
}

export const DEFAULT_MAPPING_PRESENTATION: MappingPresentation = {
  alignment: DEFAULT_MAPPING_ALIGNMENT,
  // OFF by default: an existing mapping with no config must render exactly as
  // it did before this PRD. Labels are never turned on globally.
  showLabel: false,
  labelText: '',
  labelPosition: 'above',
};

function readEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * Read a mapping's presentation settings. NEVER throws.
 *
 * Accepts the raw `config` in either form PowerSync may hand back — a JSON
 * string or an already-parsed object — because the three call sites differ.
 * Anything unparseable or unexpected degrades to the documented defaults
 * rather than breaking a printed form.
 */
export function readMappingPresentation(rawConfig: unknown): MappingPresentation {
  let cfg: Record<string, unknown> = {};
  if (typeof rawConfig === 'string') {
    try {
      const parsed: unknown = JSON.parse(rawConfig || '{}');
      if (parsed && typeof parsed === 'object') cfg = parsed as Record<string, unknown>;
    } catch {
      cfg = {};
    }
  } else if (rawConfig && typeof rawConfig === 'object') {
    cfg = rawConfig as Record<string, unknown>;
  }

  return {
    // `textAlign` is the EXISTING key — reused, not replaced, so alignment
    // already saved on live forms keeps working untouched.
    alignment: readEnum(cfg.textAlign, MAPPING_ALIGNMENTS, DEFAULT_MAPPING_ALIGNMENT),
    showLabel: cfg.showLabel === true,
    labelText: typeof cfg.labelText === 'string' ? cfg.labelText : '',
    labelPosition: readEnum(cfg.labelPosition, MAPPING_LABEL_POSITIONS, 'above'),
  };
}

/**
 * Merge presentation changes back into the mapping's config.
 *
 * Unknown keys are preserved: `config` also carries binding and drawing
 * options, and an older client saving over a newer one must not destroy them.
 */
export function writeMappingPresentation(
  rawConfig: unknown,
  patch: Partial<MappingPresentation>,
): string {
  let cfg: Record<string, unknown> = {};
  if (typeof rawConfig === 'string') {
    try {
      const parsed: unknown = JSON.parse(rawConfig || '{}');
      if (parsed && typeof parsed === 'object') cfg = { ...(parsed as Record<string, unknown>) };
    } catch {
      cfg = {};
    }
  } else if (rawConfig && typeof rawConfig === 'object') {
    cfg = { ...(rawConfig as Record<string, unknown>) };
  }

  const current = readMappingPresentation(rawConfig);
  const next = { ...current, ...patch };

  return JSON.stringify({
    ...cfg,
    textAlign: next.alignment,
    showLabel: next.showLabel,
    labelText: next.labelText,
    labelPosition: next.labelPosition,
  });
}

/**
 * The text to draw for a mapping's Form Label.
 *
 * Falls back to the canonical field label ONLY while the mapping has no text of
 * its own. Once an admin types something the stored text wins permanently —
 * including deliberate whitespace — so a later edit to the field label cannot
 * silently rewrite what the printed form says.
 *
 * Returned exactly as authored: no colon is added or stripped. "Name" and
 * "Name:" are both respected as typed.
 */
export function resolveMappingLabelText(
  presentation: MappingPresentation,
  fieldLabel: string | null | undefined,
): string {
  if (presentation.labelText !== '') return presentation.labelText;
  return fieldLabel ?? '';
}

/** Flex `justify-content` for the mapping's value. */
export function alignmentToJustify(alignment: MappingAlignment): string {
  return alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
}

/**
 * Field types whose mapped value is TEXT, and for which alignment is meaningful.
 *
 * A signature or drawing renders as a fixed-size canvas preview, so aligning it
 * inside the box does nothing observable; `rich-text` is not mappable at all
 * (PRD.322). Hiding the control is better than offering one that silently does
 * nothing.
 */
export function supportsMappingAlignment(fieldType: string | null | undefined): boolean {
  // PRD.324 — Rich Text stays excluded, now for a DIFFERENT reason. It is
  // mappable as of PRD.324, but box-level alignment would fight the document's
  // own paragraph and list alignment, and the PRD explicitly forbids
  // mapping-level alignment destroying internal formatting semantics.
  return fieldType !== 'signature' && fieldType !== 'drawing' && fieldType !== 'rich-text';
}

/**
 * Whether a Form Label can be shown for this mapping.
 *
 * Labels are useful above a signature or drawing box ("Patient Signature"), and
 * as of PRD.324 above a Rich Text flow too — where the label is drawn once at
 * the flow start and deliberately NOT repeated on continuation pages.
 *
 * Now true for every mappable type; kept as a function because the compatibility
 * question is real and has changed twice already.
 */
export function supportsMappingLabel(_fieldType: string | null | undefined): boolean {
  return true;
}
